import { useEffect, useRef, useState } from 'react';
import { chatApi, type ChatHelp, type ChatTurn } from '../api/chat.api';
import { emitAssistantChange } from '../hooks/useAssistantRefresh';

/**
 * The AI assistant panel.
 *
 * The button that opens it lives in <AppDock> - the dock owns the
 * bottom-right corner and enforces one open panel at a time, so this
 * component is CONTROLLED: it renders the panel and nothing else. It stays
 * mounted while closed, which is what keeps the conversation alive between
 * openings.
 *
 * Design decisions worth knowing:
 *  - Conversation memory lives HERE (component state) and is replayed with
 *    each request; the server is stateless. Closing the panel keeps the
 *    thread for the session; a page reload starts fresh.
 *  - '/help' never reaches the API: the module catalogue comes from
 *    GET /api/chat/help once, and rendering it is a UI concern. Tapping a
 *    module shows its example prompts; tapping an example pre-fills the
 *    input so the person can edit names/keys before sending.
 *  - If the server reports the assistant is not configured, the panel says
 *    so and the input is disabled, rather than silently doing nothing.
 */

type Message =
  | { kind: 'text'; role: 'user' | 'assistant'; content: string }
  | { kind: 'help' };

const GREETING: Message = {
  kind: 'text',
  role: 'assistant',
  content:
    "Hi! I can create tasks and sub-tasks, move them on the board, star them, change assignees, manage your calendar and lists, and look up people.\n\nType /help to see examples for each module.",
};

export function ChatWidget({
  open, onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [help, setHelp] = useState<ChatHelp | null>(null);
  const [messages, setMessages] = useState<Message[]>([GREETING]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [helpModule, setHelpModule] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    chatApi
      .help()
      .then(setHelp)
      .catch(() => setHelp({ enabled: false, modules: [] }));
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, open, helpModule]);

  // The panel ALWAYS opens, so the UI is verifiable before the Azure
  // OpenAI keys exist. When the server reports the assistant unconfigured,
  // the panel says so and the input is disabled instead of hiding the
  // whole widget.
  const configured = help === null || help.enabled;

  const historyFor = (list: Message[]): ChatTurn[] =>
    list
      .filter((m): m is Extract<Message, { kind: 'text' }> => m.kind === 'text')
      .slice(-12)
      .map(({ role, content }) => ({ role, content }));

  const send = async (raw?: string) => {
    const text = (raw ?? input).trim();
    if (text === '' || busy || !configured) return;
    setInput('');

    if (text === '/help' || text === 'help' && messages.length === 1) {
      setHelpModule(null);
      setMessages((prev) => [
        ...prev,
        { kind: 'text', role: 'user', content: text },
        { kind: 'help' },
      ]);
      return;
    }

    const before = messages;
    setMessages((prev) => [...prev, { kind: 'text', role: 'user', content: text }]);
    setBusy(true);
    try {
      const { reply, changed } = await chatApi.send(text, historyFor(before));
      setMessages((prev) => [...prev, { kind: 'text', role: 'assistant', content: reply }]);
      // A write happened server-side: tell whatever page is open to refetch.
      if (changed) emitAssistantChange();
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          kind: 'text',
          role: 'assistant',
          content:
            error instanceof Error && error.message
              ? `Sorry - ${error.message}`
              : 'Sorry - something went wrong. Please try again.',
        },
      ]);
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const pickExample = (example: string) => {
    setInput(example);
    inputRef.current?.focus();
  };

  return (
    <>
      {/* Anchored just above the footer bar (44px) that opens it. */}
      {open && (
        <div className="fixed bottom-14 right-4 z-30 flex h-[560px] max-h-[calc(100vh-7rem)] w-96 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-900 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-white">CG - To Do Assistant</p>
              <p className="text-xs text-slate-300">Type /help for what I can do</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close the assistant"
              className="rounded-lg p-1.5 text-slate-300 transition hover:bg-slate-700 hover:text-white"
            >
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-5 w-5">
                <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {!configured && (
            <div className="border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              The assistant is not configured on this server yet. Set the
              AZURE_OPENAI_* values in apps/api/.env and restart the API to
              enable chatting.
            </div>
          )}
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
            {messages.map((m, i) =>
              m.kind === 'help' ? (
                <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Pick a module
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(help?.modules ?? []).map((mod) => (
                      <button
                        key={mod.module}
                        type="button"
                        onClick={() =>
                          setHelpModule((cur) => (cur === mod.module ? null : mod.module))
                        }
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                          helpModule === mod.module
                            ? 'border-slate-900 bg-slate-900 text-white'
                            : 'border-slate-300 bg-white text-slate-700 hover:border-slate-500'
                        }`}
                      >
                        {mod.module}
                      </button>
                    ))}
                  </div>
                  {helpModule && (
                    <div className="mt-3 space-y-1.5">
                      <p className="text-xs text-slate-500">
                        {help?.modules.find((mod) => mod.module === helpModule)?.description}.
                        Tap an example to put it in the box, edit, then send:
                      </p>
                      {help?.modules
                        .find((mod) => mod.module === helpModule)
                        ?.examples.map((ex) => (
                          <button
                            key={ex}
                            type="button"
                            onClick={() => pickExample(ex)}
                            className="block w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-left text-xs text-slate-700 transition hover:border-blue-400 hover:bg-blue-50"
                          >
                            {ex}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              ) : (
                <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                  <div
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm ${
                      m.role === 'user'
                        ? 'rounded-br-sm bg-blue-600 text-white'
                        : 'rounded-bl-sm bg-slate-100 text-slate-800'
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ),
            )}
            {busy && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-sm bg-slate-100 px-3 py-2 text-sm text-slate-500">
                  Working on it…
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 p-2">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void send();
                  }
                }}
                rows={2}
                disabled={!configured}
                placeholder={configured ? 'e.g. "Mark CG-42 as done"  ·  /help' : 'Assistant not configured'}
                className="min-h-[2.5rem] flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-blue-500"
              />
              <button
                type="button"
                onClick={() => void send()}
                disabled={busy || input.trim() === '' || !configured}
                aria-label="Send"
                className="rounded-xl bg-blue-600 p-2.5 text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-5 w-5">
                  <path d="M3 10l13-6-4 6 4 6-13-6z" fill="currentColor" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
